import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  Briefcase,
  AlertCircle,
} from 'lucide-react';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const DUTY_COLORS = {
  front_desk: 'bg-blue-100 text-blue-700 border-blue-300',
  coaching: 'bg-green-100 text-green-700 border-green-300',
  academy_management: 'bg-purple-100 text-purple-700 border-purple-300'
};

const StaffSchedule = () => {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' or 'past'

  useEffect(() => {
    fetchShifts();
  }, [viewMode]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === 'upcoming' 
        ? '/staff/shifts' 
        : '/staff/shifts?show_past=true';
      const response = await api.get(endpoint);
      setShifts(response.data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const calculateDuration = (start, end) => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const totalMinutes = (eh * 60 + em) - (sh * 60 + sm);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };

  const getDateBadge = (dateStr) => {
    if (isToday(dateStr)) return <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Today</span>;
    if (isTomorrow(dateStr)) return <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">Tomorrow</span>;
    return null;
  };

  // Group shifts by date
  const groupedShifts = shifts.reduce((acc, shift) => {
    const date = shift.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(shift);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout title="My Schedule">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Schedule">
      <div className="space-y-6" data-testid="staff-schedule-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">View your assigned shifts and work schedule</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={viewMode === 'upcoming' ? 'default' : 'outline'}
              onClick={() => setViewMode('upcoming')}
              className={viewMode === 'upcoming' ? 'bg-blue-500 hover:bg-blue-600' : ''}
              data-testid="upcoming-btn"
            >
              Upcoming
            </Button>
            <Button
              variant={viewMode === 'past' ? 'default' : 'outline'}
              onClick={() => setViewMode('past')}
              className={viewMode === 'past' ? 'bg-blue-500 hover:bg-blue-600' : ''}
              data-testid="past-btn"
            >
              Past Shifts
            </Button>
          </div>
        </div>

        {/* Shifts List */}
        {Object.keys(groupedShifts).length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="p-8 lg:p-12 text-center">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                No {viewMode === 'upcoming' ? 'Upcoming' : 'Past'} Shifts
              </h3>
              <p className="text-slate-500">
                {viewMode === 'upcoming' 
                  ? "You don't have any scheduled shifts yet. Check back later or contact your admin."
                  : "No past shifts found in your history."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedShifts).map(([date, dayShifts]) => (
              <Card key={date} className="border border-slate-200" data-testid={`shift-day-${date}`}>
                <CardHeader className="border-b border-slate-100 py-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    {formatDate(date)}
                    {getDateBadge(date)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 divide-y divide-slate-100">
                  {dayShifts.map((shift) => (
                    <div 
                      key={shift.id} 
                      className="p-3 lg:p-4 flex items-center gap-3 lg:gap-4 hover:bg-slate-50 transition-colors"
                      data-testid={`shift-item-${shift.id}`}
                    >
                      {/* Time Block */}
                      <div className="flex-shrink-0 text-center bg-slate-100 rounded-lg px-2.5 lg:px-4 py-1.5 lg:py-2 min-w-[90px] lg:min-w-[120px]">
                        <p className="text-xs lg:text-sm font-semibold text-slate-900">
                          {formatTime(shift.start_time)}
                        </p>
                        <p className="text-[10px] lg:text-xs text-slate-400">to</p>
                        <p className="text-xs lg:text-sm font-semibold text-slate-900">
                          {formatTime(shift.end_time)}
                        </p>
                      </div>

                      {/* Shift Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${DUTY_COLORS[shift.duty_type]}`}>
                            {DUTY_LABELS[shift.duty_type]}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {calculateDuration(shift.start_time, shift.end_time)}
                          </span>
                        </div>
                        {shift.notes && (
                          <p className="text-sm text-slate-600 mt-2 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            {shift.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary Card */}
        {viewMode === 'upcoming' && shifts.length > 0 && (
          <Card className="border border-slate-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">
                    <strong>Upcoming Schedule:</strong> You have {shifts.length} shift{shifts.length !== 1 ? 's' : ''} scheduled
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Total hours: {shifts.reduce((acc, s) => {
                      const [sh, sm] = s.start_time.split(':').map(Number);
                      const [eh, em] = s.end_time.split(':').map(Number);
                      return acc + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                    }, 0).toFixed(1)}h
                  </p>
                </div>
                <Briefcase className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffSchedule;
